"use client";

import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Presence() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [activeOffice, setActiveOffice] = useState<number>(0);
  const [isZh, setIsZh] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsZh(localStorage.getItem("elite-language") === "zh-Hant");
    }
  }, []);
  const offices = [{
    city: isZh ? "香港" : "Hong Kong",
    timezone: "HKT (UTC+8)",
    address: "Unit 702, 7/F., Concordia Plaza, 1 Science Museum Road, Tsim Sha Tsui, Kowloon, Hong Kong",
    x: 76,
    y: 58
  }, {
    city: isZh ? "福建" : "Fujian",
    timezone: "CST (UTC+8)",
    address: "Jinjiang, China",
    x: 75,
    y: 55
  }, {
    city: isZh ? "多倫多" : "Toronto",
    timezone: "EST (UTC−5)",
    address: "Toronto, ON, Canada",
    x: 28,
    y: 38
  }];
  return <section data-section-id="11420" id="our-global-presence" ref={sectionRef} className="bg-background py-24 md:py-32 border-y border-border">
      <div className="mx-auto max-w-[1240px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <h2 className={`font-default text-foreground font-semibold leading-[1.08] tracking-tight transition-all duration-[900ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2rem, 2.8vw + 0.75rem, 3.5rem)"
          }}>
              {isZh ? "製造實力，" : "Manufacturing, within "}
              <span className={`italic transition-opacity duration-[700ms] ${isVisible ? "opacity-100" : "opacity-0"}`} style={{
              transitionDelay: isVisible ? "200ms" : "0ms"
            }}>
                {isZh ? "近在咫尺" : "reach"}
              </span>
              {isZh ? "。" : "."}
            </h2>

            <p className={`mt-8 text-foreground/90 font-default font-normal leading-[1.55] max-w-[48ch] transition-all duration-[720ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(1.125rem, 0.4vw + 1.05rem, 1.3125rem)",
            transitionDelay: isVisible ? "200ms" : "0ms"
          }}>
              {isZh ? "Elite Group Corporation Limited 為創意設計與工業規模化生產搭建無縫橋樑。憑藉設於香港、中國內地、加拿大及阿聯酋的戰略樞紐，我們確保您的產品精準符合全球合規標準及特定市場的認證要求。" : "Elite Group Corporation Limited bridges the gap between creative design and industrial-scale production. With strategic hubs in Hong Kong, China, Canada, and the UAE, we ensure your products meet global compliance standards and market-specific certifications with precision."}
            </p>

            <ul className="mt-12 border-t border-border">
              {offices.map((office, i) => <li key={i} data-index={i} onMouseEnter={() => setActiveOffice(i)} className={`group relative border-b border-border transition-colors duration-200 cursor-pointer ${activeOffice === i ? "bg-secondary" : ""}`}>
                  <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-px bg-primary transition-all duration-300 ease-out ${activeOffice === i ? "w-6" : "w-0"}`} aria-hidden="true" />
                  <div className={`flex items-start justify-between gap-6 py-7 transition-all duration-200 ${activeOffice === i ? "pl-9" : "pl-0"} pr-2`}>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-default font-semibold text-foreground text-lg leading-[1.4] tracking-tight">
                        {office.city}
                      </h4>
                      <p className="font-mono text-muted-foreground mt-2 text-[0.8125rem] leading-[1.5]" style={{
                    letterSpacing: "0.04em"
                  }}>
                        {office.timezone}
                      </p>
                      <p className="font-default text-muted-foreground mt-1 text-[0.8125rem] leading-[1.5]">
                        {office.address}
                      </p>
                    </div>
                    <Link to="#" className="shrink-0 font-default italic text-foreground text-sm whitespace-nowrap relative pb-0.5 inline-flex items-center gap-1.5 mt-1">
                      <span className="relative">
                        {isZh ? "據點詳情" : "Location details"}
                        <span className="absolute left-0 -bottom-0.5 h-px w-full bg-primary origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out" />
                      </span>
                      <ArrowRight className="size-3.5" strokeWidth={1.25} />
                    </Link>
                  </div>
                </li>)}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className={`relative border border-border bg-background overflow-hidden transition-all duration-[720ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: isVisible ? "300ms" : "0ms"
          }}>
              <div className="relative w-full aspect-[16/9] lg:aspect-auto lg:h-[640px]">
                <svg viewBox="0 0 1000 560" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" aria-label={isZh ? "顯示 Elite Group Corporation Limited 辦事處據點之世界地圖" : "World map showing Elite Group Corporation Limited office locations"}>
                  <rect width="1000" height="560" fill="hsl(0 0% 98%)" />

                  <g fill="hsl(0 0% 90%)" stroke="hsl(0 0% 80%)" strokeWidth="0.75">
                    <path d="M 80,120 L 140,90 L 220,95 L 280,110 L 320,140 L 340,180 L 330,220 L 300,250 L 260,270 L 220,290 L 180,310 L 140,300 L 110,270 L 90,230 L 75,180 Z" />
                    <path d="M 220,290 L 250,310 L 270,340 L 260,360 L 240,350 L 230,320 Z" />
                    <path d="M 260,360 L 290,370 L 310,400 L 320,440 L 310,480 L 290,510 L 270,520 L 260,490 L 250,450 L 248,410 Z" />
                    <path d="M 470,130 L 510,115 L 545,120 L 565,140 L 555,165 L 530,180 L 500,180 L 475,170 L 460,150 Z" />
                    <path d="M 490,200 L 540,195 L 580,220 L 600,260 L 605,310 L 590,360 L 560,395 L 530,400 L 500,380 L 485,340 L 480,290 L 482,240 Z" />
                    <path d="M 565,140 L 620,120 L 700,110 L 780,125 L 840,150 L 870,180 L 870,220 L 840,250 L 800,270 L 750,275 L 700,265 L 650,250 L 610,225 L 580,200 L 565,170 Z" />
                    <path d="M 760,300 L 800,295 L 830,310 L 825,330 L 790,335 L 760,325 Z" />
                    <path d="M 680,250 L 705,255 L 715,290 L 700,315 L 685,310 L 675,285 Z" />
                    <path d="M 800,380 L 860,375 L 895,395 L 895,425 L 870,445 L 825,445 L 795,425 L 790,400 Z" />
                    <path d="M 465,130 L 478,125 L 480,145 L 470,150 Z" />
                    <path d="M 380,80 L 420,75 L 440,95 L 430,115 L 400,120 L 380,105 Z" />
                  </g>

                  {offices.map((office, i) => <text key={`label-${i}`} x={office.x * 10} y={office.y * 5.6 - 14} fill={activeOffice === i ? "hsl(44 92% 53%)" : "hsl(0 0% 10%)"} fontSize="11" fontFamily="Courier New, monospace" letterSpacing="1.2" textAnchor="middle" className="transition-colors duration-200 uppercase" style={{
                  fontWeight: 600
                }}>
                      {office.city}
                    </text>)}

                  {offices.map((office, i) => {
                  const cx = office.x * 10;
                  const cy = office.y * 5.6;
                  const delay = i * 200;
                  return <g key={`marker-${i}`} onClick={() => setActiveOffice(i)} className="cursor-pointer">
                        {isVisible && <circle cx={cx} cy={cy} r="8" fill="none" stroke="hsl(44 92% 53%)" strokeWidth="1" opacity="0">
                            <animate attributeName="r" from="8" to="20" dur="1.2s" begin={`${delay}ms`} fill="freeze" calcMode="spline" keySplines="0.2 0.7 0.2 1" />
                            <animate attributeName="opacity" values="0;0.8;0" dur="1.2s" begin={`${delay}ms`} fill="freeze" />
                          </circle>}
                        <circle cx={cx} cy={cy} r="8" fill="none" stroke="hsl(44 92% 53%)" strokeWidth="1" className="transition-all duration-300" style={{
                      transform: activeOffice === i ? "scale(1.15)" : "scale(1)",
                      transformOrigin: `${cx}px ${cy}px`
                    }} />
                        <circle cx={cx} cy={cy} r="3" fill="hsl(44 92% 53%)" />
                        <circle cx={cx} cy={cy} r="18" fill="transparent" />
                      </g>;
                })}
                </svg>

                <div className="absolute top-5 left-5 pointer-events-none">
                  <p className="font-mono text-foreground text-[0.6875rem] uppercase" style={{
                  letterSpacing: "0.16em",
                  opacity: 0.7
                }}>
                    {isZh ? "全球營運網絡" : "Global operational network"}
                  </p>
                </div>

                <div className="absolute bottom-5 right-5 pointer-events-none flex items-center gap-2.5">
                  <span className="block size-2 bg-primary" />
                  <p className="font-mono text-foreground text-[0.6875rem] uppercase" style={{
                  letterSpacing: "0.16em",
                  opacity: 0.7
                }}>
                    {isZh ? "Elite Group 營運樞紐" : "Elite Group Hub"}
                  </p>
                </div>
              </div>
            </div>

            <p className="mt-4 font-default text-muted-foreground text-[0.8125rem] leading-[1.5]">
              {isZh ? "整合製造與合規服務，遍及全球四大戰略樞紐。" : "Integrated manufacturing and compliance services across four strategic global locations."}
            </p>
          </div>
        </div>
      </div>
    </section>;
}